import { useState } from 'react';
import { useQuery } from 'react-query';
import { api } from '../../services/api';
import { Panel, PanelHeader, PanelBody } from '../ui/Panel';
import { ErrorNotice, SkeletonRows } from '../ui/States';
import { errorMessage, selectClass } from '../../lib/programs';
import { torontoToday } from '../../lib/events';
import { DayDietaryView, headRow, td, th, weekDay } from './logistics';

/** One day's meals for the people at the venue: headcounts and dietary needs (names for admins). */
export default function DayDietary({
  eventId,
  days,
  date: fixed,
}: {
  eventId: string;
  canEdit?: boolean;
  days: string[];
  date?: string;
}) {
  const today = torontoToday();
  const [date, setDate] = useState(fixed ?? (days.includes(today) ? today : days[0]));
  const day = useQuery<DayDietaryView>(
    ['event-day-dietary', eventId, date],
    async () => (await api.get(`/events/${eventId}/catering/day/${date}`)).data.data,
    { enabled: Boolean(date) },
  );
  return (
    <Panel>
      <PanelHeader
        title={date === today ? 'Meals today' : 'Meals'}
        note={day.data ? `${day.data.attendees} expected in person` : undefined}
      >
        {!fixed && days.length > 1 ? (
          <select className={selectClass} value={date} onChange={(e) => setDate(e.target.value)}>
            {days.map((d) => (
              <option key={d} value={d}>
                {weekDay(d)}
              </option>
            ))}
          </select>
        ) : null}
      </PanelHeader>
      <PanelBody>
        {day.isLoading ? (
          <SkeletonRows rows={4} />
        ) : day.error || !day.data ? (
          <ErrorNotice>{errorMessage(day.error, 'Could not load the day.')}</ErrorNotice>
        ) : (
          <>
            <div className="flex gap-4 flex-wrap text-table">
              {day.data.meals.map((m) => (
                <div key={m.key}>
                  <span className="text-ink-2">{m.label}</span> <span className="font-semibold">{m.count}</span>
                </div>
              ))}
            </div>
            <div className="text-small mt-3">
              {day.data.dietary.length
                ? day.data.dietary.map((g) => `${g.tag} ${g.count}`).join(' · ')
                : 'No dietary needs given.'}
            </div>
            {day.data.dinner?.choices.length ? (
              <div className="text-small mt-1">
                Dinner: {day.data.dinner.choices.map((c) => `${c.choice} ${c.count}`).join(' · ')}
              </div>
            ) : null}
            {day.data.people?.length ? (
              <table className="w-full text-small mt-3">
                <thead>
                  <tr className={headRow}>
                    <th className={th}>Name</th>
                    <th className={th}>Dietary</th>
                    <th className={th}>Dinner</th>
                  </tr>
                </thead>
                <tbody>
                  {day.data.people.map((p) => (
                    <tr key={p.inviteeId} className="border-b border-line">
                      <td className={td}>{p.name}</td>
                      <td className={td}>{p.dietary ?? '—'}</td>
                      <td className={td}>
                        {p.dinner?.attending === false ? 'Not attending' : (p.dinner?.choice ?? '—')}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : null}
          </>
        )}
      </PanelBody>
    </Panel>
  );
}
